import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Database, Download, Trash2, RefreshCw, CheckCircle, AlertCircle, Clock, HardDrive, RotateCcw, X, AlertTriangle } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminBackups = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  
  // Restore state
  const [restoreModal, setRestoreModal] = useState(null); // backup object or null
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [restoreDangerAck, setRestoreDangerAck] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreStatus, setRestoreStatus] = useState(null);

  const getToken = useCallback(() => {
    return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/backups/mongo/status`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Error fetching backup status:', error);
    }
  }, [getToken]);

  const fetchBackups = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/backups/mongo/list`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBackups(data.backups || []);
      }
    } catch (error) {
      console.error('Error fetching backups:', error);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchStatus();
    fetchBackups();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchStatus();
      fetchBackups();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchStatus, fetchBackups]);

  const createBackup = async () => {
    if (status?.next_backup_available_in_seconds > 0) {
      toast.error(`Veuillez attendre ${status.next_backup_available_in_seconds} secondes`);
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/backups/mongo/create`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Backup créé: ${data.filename}`);
        fetchBackups();
        fetchStatus();
      } else if (response.status === 429) {
        const data = await response.json();
        toast.error(data.detail || 'Rate limit atteint');
      } else if (response.status === 409) {
        toast.error('Un backup est déjà en cours');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur lors de la création du backup');
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Erreur de connexion');
    } finally {
      setCreating(false);
    }
  };

  const downloadBackup = async (backup) => {
    try {
      console.log('[Backup] Starting download for:', backup.backup_id, backup.filename);
      toast.info('Téléchargement en cours...');
      
      const token = getToken();
      
      // Use direct URL with token as query param (iOS Safari compatible)
      const downloadUrl = `${API_BASE_URL}/api/admin/backups/mongo/${backup.backup_id}/download-direct?token=${encodeURIComponent(token)}`;
      
      // Use an anchor element to trigger download — avoids pop-up blockers
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = backup.filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      toast.success('Téléchargement lancé');
    } catch (error) {
      console.error('[Backup] Error downloading backup:', error);
      toast.error('Erreur de téléchargement: ' + error.message);
    }
  };

  const deleteBackup = async (backup) => {
    if (!window.confirm(`Supprimer le backup ${backup.filename} ?`)) {
      return;
    }

    setDeletingId(backup.backup_id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/backups/mongo/${backup.backup_id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      if (response.ok) {
        toast.success('Backup supprimé');
        fetchBackups();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error('Erreur de suppression');
    } finally {
      setDeletingId(null);
    }
  };

  // === RESTORE FUNCTIONS ===
  
  const openRestoreModal = (backup) => {
    setRestoreModal(backup);
    setRestoreConfirm('');
    setRestoreDangerAck(false);
    setRestoreStatus(null);
  };

  const closeRestoreModal = () => {
    if (!restoring) {
      setRestoreModal(null);
      setRestoreConfirm('');
      setRestoreDangerAck(false);
    }
  };

  const fetchRestoreStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/backups/mongo/restore/status`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRestoreStatus(data);
        return data;
      }
    } catch (error) {
      console.error('Error fetching restore status:', error);
    }
    return null;
  };

  const startRestore = async () => {
    if (restoreConfirm !== 'RESTORE') {
      toast.error('Tapez exactement RESTORE pour confirmer');
      return;
    }
    if (!restoreDangerAck) {
      toast.error('Cochez la case de confirmation');
      return;
    }

    setRestoring(true);
    setRestoreStatus({ state: 'running', message: 'Démarrage...' });

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/backups/mongo/${restoreModal.backup_id}/restore`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            confirm: restoreConfirm,
            danger_ack: restoreDangerAck
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        setRestoreStatus({ state: 'error', message: data.detail || 'Erreur' });
        toast.error(data.detail || 'Erreur lors du restore');
        setRestoring(false);
        return;
      }

      // Poll for status
      const pollStatus = async () => {
        const status = await fetchRestoreStatus();
        if (status) {
          setRestoreStatus(status);
          if (status.state === 'running') {
            setTimeout(pollStatus, 2000);
          } else {
            setRestoring(false);
            if (status.state === 'success') {
              toast.success('Restore terminé avec succès');
              fetchBackups(); // Refresh list to show pre-restore backup
            } else if (status.state === 'error') {
              toast.error(status.message || 'Échec du restore');
            }
          }
        } else {
          setTimeout(pollStatus, 2000);
        }
      };

      setTimeout(pollStatus, 1000);

    } catch (error) {
      console.error('Error starting restore:', error);
      setRestoreStatus({ state: 'error', message: error.message });
      toast.error('Erreur de connexion');
      setRestoring(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    
    if (diff <= 0) return 'Expiré';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pb-32" data-testid="admin-backups-page">
      <PageHeader title="Backups MongoDB" showBack={true} />

      <div className="container max-w-4xl mx-auto px-4 py-6">
        {/* Status Card */}
        <Card className="mb-6" data-testid="backup-status-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Database className="h-6 w-6 text-blue-600" />
                <div>
                  <CardTitle>État du système</CardTitle>
                  <CardDescription>MongoDB Backup V1</CardDescription>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { fetchStatus(); fetchBackups(); }}
                data-testid="refresh-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Actualiser
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {status ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-2">
                  {status.mongodump_available ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  )}
                  <span className="text-sm">mongodump</span>
                </div>
                <div className="flex items-center gap-2">
                  {status.mongorestore_available ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  )}
                  <span className="text-sm">mongorestore</span>
                </div>
                <div className="flex items-center gap-2">
                  <HardDrive className="h-5 w-5 text-blue-500" />
                  <span className="text-sm">{status.backups_count} backup(s)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-orange-500" />
                  <span className="text-sm">TTL: {status.ttl_hours}h</span>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500">Chargement...</div>
            )}
          </CardContent>
        </Card>

        {/* Create Backup Card */}
        <Card className="mb-6" data-testid="create-backup-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Créer un backup
            </CardTitle>
            <CardDescription>
              Génère une archive compressée (.gz) de la base MongoDB
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {status?.backup_in_progress ? (
                  <span className="text-orange-600">⏳ Backup en cours...</span>
                ) : status?.next_backup_available_in_seconds > 0 ? (
                  <span className="text-orange-600">
                    ⏱️ Prochain backup disponible dans {status.next_backup_available_in_seconds}s
                  </span>
                ) : (
                  <span className="text-green-600">✅ Prêt pour un nouveau backup</span>
                )}
              </div>
              <Button
                onClick={createBackup}
                disabled={creating || status?.backup_in_progress || status?.next_backup_available_in_seconds > 0}
                data-testid="create-backup-btn"
              >
                {creating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <Database className="h-4 w-4 mr-2" />
                    Créer backup
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Backups List */}
        <Card data-testid="backups-list-card">
          <CardHeader>
            <CardTitle>Backups disponibles</CardTitle>
            <CardDescription>
              Les backups expirent automatiquement après {status?.ttl_hours || 24} heures
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Chargement...</div>
            ) : backups.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Aucun backup disponible. Créez votre premier backup ci-dessus.
              </div>
            ) : (
              <div className="space-y-3">
                {backups.map((backup) => (
                  <div
                    key={backup.backup_id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border"
                    data-testid={`backup-item-${backup.backup_id}`}
                  >
                    <div className="flex-1">
                      <div className="font-medium text-sm flex items-center gap-2">
                        {backup.filename}
                        {backup.type === 'pre_restore' && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                            Pre-restore
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        <span className="mr-4">📅 {formatDate(backup.created_at)}</span>
                        <span className="mr-4">💾 {formatSize(backup.size_bytes)}</span>
                        <span className="text-orange-600">⏳ Expire dans {getTimeRemaining(backup.expires_at)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openRestoreModal(backup)}
                        disabled={status?.restore_in_progress || restoring}
                        className="inline-flex items-center justify-center rounded-md bg-amber-500 px-3 py-2 text-sm font-medium text-white hover:bg-amber-600 cursor-pointer shadow-sm disabled:opacity-50"
                        data-testid={`restore-btn-${backup.backup_id}`}
                        title="Restaurer ce backup"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          console.log('[AdminBackups] Download clicked for:', backup.backup_id);
                          downloadBackup(backup);
                        }}
                        className="inline-flex items-center justify-center rounded-md bg-white border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 cursor-pointer shadow-sm"
                        data-testid={`download-btn-${backup.backup_id}`}
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          console.log('[AdminBackups] Delete clicked for:', backup.backup_id);
                          deleteBackup(backup);
                        }}
                        disabled={deletingId === backup.backup_id}
                        className="inline-flex items-center justify-center rounded-md bg-red-500 px-3 py-2 text-sm font-medium text-white hover:bg-red-600 cursor-pointer shadow-sm disabled:opacity-50"
                        data-testid={`delete-btn-${backup.backup_id}`}
                      >
                        {deletingId === backup.backup_id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-2">ℹ️ Informations</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Les backups sont stockés temporairement sur le serveur</li>
                <li>Expiration automatique après {status?.ttl_hours || 24} heures</li>
                <li>Rate limit backup: 1 toutes les {status?.rate_limit_seconds ? status.rate_limit_seconds / 60 : 5} minutes</li>
                <li>Rate limit restore: 1 toutes les {status?.restore_rate_limit_seconds ? status.restore_rate_limit_seconds / 60 : 30} minutes</li>
                <li>Format: archive MongoDB compressée (gzip)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Restore Status Banner */}
        {(status?.restore_in_progress || restoreStatus?.state === 'running') && (
          <Card className="mt-6 bg-amber-50 border-amber-300">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <RefreshCw className="h-5 w-5 text-amber-600 animate-spin" />
                <div>
                  <p className="font-semibold text-amber-800">Restore en cours...</p>
                  <p className="text-sm text-amber-700">{restoreStatus?.message || 'Veuillez patienter'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Restore Confirmation Modal */}
      {restoreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-red-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-white" />
                <h3 className="text-lg font-bold text-white">Restore Database</h3>
              </div>
              <button
                onClick={closeRestoreModal}
                disabled={restoring}
                className="text-white/80 hover:text-white disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Status display during restore */}
              {restoreStatus && (restoreStatus.state === 'running' || restoreStatus.state === 'success' || restoreStatus.state === 'error') ? (
                <div className="space-y-4">
                  {restoreStatus.state === 'running' && (
                    <div className="text-center py-8">
                      <RefreshCw className="h-12 w-12 text-amber-500 animate-spin mx-auto mb-4" />
                      <p className="text-gray-800 font-medium">{restoreStatus.message}</p>
                      <p className="text-sm text-gray-500 mt-2">Ne fermez pas cette fenêtre</p>
                    </div>
                  )}
                  
                  {restoreStatus.state === 'success' && (
                    <div className="text-center py-6">
                      <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                      <p className="text-green-800 font-medium text-lg">Restore terminé !</p>
                      <p className="text-gray-600 mt-2">{restoreStatus.message}</p>
                      {restoreStatus.pre_restore_backup_id && (
                        <p className="text-sm text-gray-500 mt-4 bg-gray-100 p-3 rounded">
                          🛡️ Backup de sécurité créé :<br />
                          <code className="text-xs">{restoreStatus.pre_restore_backup_id}</code>
                        </p>
                      )}
                      <Button
                        onClick={() => {
                          closeRestoreModal();
                          window.location.reload();
                        }}
                        className="mt-6 bg-green-600 hover:bg-green-700"
                      >
                        Rafraîchir l'application
                      </Button>
                    </div>
                  )}
                  
                  {restoreStatus.state === 'error' && (
                    <div className="text-center py-6">
                      <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                      <p className="text-red-800 font-medium text-lg">Échec du restore</p>
                      <p className="text-gray-600 mt-2">{restoreStatus.message}</p>
                      {restoreStatus.pre_restore_backup_id && (
                        <p className="text-sm text-green-600 mt-4 bg-green-50 p-3 rounded">
                          ✅ Un backup de sécurité a été créé avant l'échec
                        </p>
                      )}
                      <Button
                        onClick={closeRestoreModal}
                        variant="outline"
                        className="mt-6"
                      >
                        Fermer
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                /* Confirmation form */
                <div className="space-y-4">
                  {/* Warning message */}
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 font-medium">⚠️ Action dangereuse</p>
                    <p className="text-red-700 text-sm mt-1">
                      Cette opération <strong>remplace entièrement</strong> la base de données actuelle 
                      avec le contenu du backup sélectionné.
                    </p>
                  </div>

                  {/* Backup info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Backup à restaurer :</p>
                    <p className="font-mono text-sm mt-1">{restoreModal.filename}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Créé le {formatDate(restoreModal.created_at)} • {formatSize(restoreModal.size_bytes)}
                    </p>
                  </div>

                  {/* Safety info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800 text-sm">
                      🛡️ Un backup automatique sera créé avant le restore pour permettre un rollback si nécessaire.
                    </p>
                  </div>

                  {/* Confirmation input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tapez <strong>RESTORE</strong> pour confirmer :
                    </label>
                    <input
                      type="text"
                      value={restoreConfirm}
                      onChange={(e) => setRestoreConfirm(e.target.value)}
                      placeholder="RESTORE"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 font-mono"
                      disabled={restoring}
                      data-testid="restore-confirm-input"
                    />
                  </div>

                  {/* Danger acknowledgment */}
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={restoreDangerAck}
                      onChange={(e) => setRestoreDangerAck(e.target.checked)}
                      disabled={restoring}
                      className="mt-1 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                      data-testid="restore-danger-checkbox"
                    />
                    <span className="text-sm text-gray-700">
                      Je comprends que cette action <strong>écrase toutes les données actuelles</strong> et 
                      ne peut pas être annulée facilement.
                    </span>
                  </label>

                  {/* Action buttons */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={closeRestoreModal}
                      disabled={restoring}
                      className="flex-1"
                    >
                      Annuler
                    </Button>
                    <button
                      type="button"
                      onClick={startRestore}
                      disabled={restoring || restoreConfirm !== 'RESTORE' || !restoreDangerAck}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium bg-red-700 text-white hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="restore-confirm-btn"
                    >
                      {restoring ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          En cours...
                        </>
                      ) : (
                        'Restore now'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBackups;
